import { useCallback, useEffect, useRef, useState } from 'react'
import '../App.css'
import './LoginPage.css'
import VoiceChat from '../components/VoiceChat'

/** What an employee sees when their link cannot (or can no longer) start a session. */
export const LINK_MESSAGES = {
  used:     'You have already used this session link. Each link is good for one session — please wait for a new link to be sent to you.',
  expired:  'This session link has expired. Please wait for a new link to be sent to you.',
  revoked:  'This session link has been cancelled. Please wait for a new link to be sent to you.',
  invalid:  'This link is not valid. Please open the full link from your email, or wait for a new one to be sent to you.',
  ended:    'Your session has ended. Please wait for a new link to be sent to you.',
} as const

export function LinkMessage({ title, message }: { title: string; message: string }) {
  return (
    <div className="login-page">
      <div className="login-card" role="alertdialog" aria-labelledby="link-msg-title" aria-describedby="link-msg-body">
        <div className="login-card__header">
          <h1 className="login-card__title">Dräger AI Product Training</h1>
          <p className="login-card__subtitle" id="link-msg-title">{title}</p>
        </div>
        <div className="login-card__notice" id="link-msg-body">{message}</div>
      </div>
    </div>
  )
}

/**
 * The chat for an employee (magic link). Locked to that employee: no selector,
 * no operator or admin navigation; the server enforces it on /ws/voice.
 * A link is good for ONE session: when the call ends, the chat is replaced by a
 * "session complete" message, so there is nothing left to click.
 */
export function EmployeeChatView({ employeeName, beforeStart }: {
  employeeName: string
  beforeStart?: () => Promise<void>
}) {
  const [phase, setPhase] = useState<'ready' | 'complete' | 'refused'>('ready')
  const [refusal, setRefusal] = useState('')
  const hadCall = useRef(false)

  const onActiveChange = useCallback((active: boolean) => {
    if (active) hadCall.current = true
    else if (hadCall.current) setPhase(p => (p === 'ready' ? 'complete' : p))
  }, [])
  const onLinkUsed = useCallback((message: string) => { setRefusal(message); setPhase('refused') }, [])

  if (phase === 'refused') return <LinkMessage title="Link already used" message={refusal || LINK_MESSAGES.used} />
  if (phase === 'complete') {
    return <LinkMessage title={`Thank you, ${employeeName}!`}
      message="Your session is complete and this link has now been used. You will receive a new link for your next session." />
  }

  return (
    <div className="app">
      <div className="app-card">
        <header className="app-header">
          <div className="app-header__logo-box">
            <img src="/intminds-170x72.png" alt="Intminds" className="app-header__logo-img" />
          </div>
          <h1>Dräger AI Product Training</h1>
          <div className="app-header__employee">
            <span className="app-header__subtitle">Employee: <strong>{employeeName}</strong></span>
          </div>
          <div className="app-header__divider" />
        </header>

        <VoiceChat beforeStart={beforeStart} onLinkUsed={onLinkUsed} onActiveChange={onActiveChange} />
      </div>
    </div>
  )
}

/** /session — a redeemed link whose one call has not started yet (e.g. after a refresh). */
export default function EmployeeSessionPage() {
  const [name,    setName]    = useState<string | null>(null)
  const [blocked, setBlocked] = useState<{ title: string; message: string } | null>(null)

  useEffect(() => {
    fetch('/api/employee-access/me', { credentials: 'include' })
      .then(async res => {
        const body = await res.json().catch(() => ({})) as { employee?: { name: string }; callUsed?: boolean }
        if (!res.ok || !body.employee) return setBlocked({ title: 'Session ended', message: LINK_MESSAGES.ended })
        if (body.callUsed) return setBlocked({ title: 'Link already used', message: LINK_MESSAGES.used })
        setName(body.employee.name)
      })
      .catch(() => setBlocked({ title: 'Session ended', message: LINK_MESSAGES.ended }))
  }, [])

  if (blocked) return <LinkMessage {...blocked} />
  if (!name) return null
  return <EmployeeChatView employeeName={name} />
}
